import { ColumnDef, Row, Table } from "@tanstack/react-table"
import { ArrowDownRight, ArrowUpLeft, Check, CircleX } from "lucide-react"
import PendingPaymentStatus from "../../../ui/status-icons/PendingPaymentStatus"
import PendingWithdrawStatus from "../../../ui/status-icons/PendingWithdrawStatus"
import RejectedWithdrawStatus from "../../../ui/status-icons/RejectedWithdrawStatus"
import { Heading6 } from "../../../ui/texts/TextStyle"

import { TableMovement, TableMovementType } from "../data/types"

export const movementTranscationStatus = ["PENDING", "PROCESSED", "REJECTED"]

export type movementTranscationStatus =
	(typeof movementTranscationStatus)[number]

export function TypeColumn(): ColumnDef<TableMovement> {
	return {
		accessorKey: "status",
		header: () => (
			<div className="text-left text-h6 items-center flex flex-row gap-2">
				<Check className="size-icon" />
				Movimiento
			</div>
		),
		filterFn: (row: Row<TableMovement>, id: string, value: any) => {
			// Handle array of movement states with types
			if (Array.isArray(value)) {
				return value.some((filter) => {
					if (typeof filter === "string") {
						return row.getValue(id) === filter
					}
					// Handle movement type+state objects
					return (
						filter.state === row.getValue(id) &&
						filter.type === row.original.type
					)
				})
			}
			return false
		},
		cell: ({ row }: { row: Row<TableMovement> }) => {
			const status = row.original.status

			const type = row.original.type

			return <TypeCell status={status} type={type} />
		},
	}
}

export const inboundStatusIcons: { [key: string]: JSX.Element } = {
	PROCESSED: <ArrowDownRight className="w-7 h-7 stroke-[#1FC801]" />,
	UNMATCHED: <ArrowDownRight className="w-7 h-7 stroke-icon-warning" />,
}

export const outboundStatusIcons: { [key: string]: JSX.Element } = {
	PROCESSED: <ArrowUpLeft className="w-7 h-7 stroke-icon-error" />,
	PENDING: <PendingWithdrawStatus className="w-7 h-7" />,
	REJECTED: <RejectedWithdrawStatus />,
}

export const inboundStatusTranslations: { [key: string]: string } = {
	PROCESSED: "Entrante",
	UNMATCHED: "Entrante",
}

export const outboundStatusTranslations: { [key: string]: string } = {
	PENDING: "Saliente",
	PROCESSED: "Saliente",
	REJECTED: "Saliente",
}

export const inboundStatusDescriptions: { [key: string]: string } = {
	PROCESSED: "El pago se procesó correctamente",
	UNMATCHED: "Se recibió el dinero, pero no identificamos la orden",
}

export const outboundStatusDescriptions: { [key: string]: string } = {
	PROCESSED: "El retiro se completó exitosamente",
	PENDING: "El retiro está pendiente de procesamiento",
	REJECTED: "El retiro fue rechazado",
}

export const inboundStatusTags: { [key: string]: string } = {
	PROCESSED: "Recbido",
	UNMATCHED: "Sin Asignar",
}

export const outboundStatusTags: { [key: string]: string } = {
	PROCESSED: "Completado",
	PENDING: "Pendiente",
	REJECTED: "Rechazado",
} 

export function getStatusMappings(type: TableMovementType) {
	return type === "INBOUND"
		? {
				icons: inboundStatusIcons,
				translations: inboundStatusTranslations,
				descriptions: inboundStatusDescriptions,
				tags: inboundStatusTags,
		  }
		: {
				icons: outboundStatusIcons,
				translations: outboundStatusTranslations,
				descriptions: outboundStatusDescriptions,
				tags: outboundStatusTags,
		  }
}

export function TypeCell({
	status,
	type,
}: {
	status: (typeof movementTranscationStatus)[number]
	type: TableMovementType
}) {
	const { icons, translations } = getStatusMappings(type)

	return (
		<div className="flex items-center w-fit ">
			{icons[status]}
			<Heading6 className="capitalize ml-2 font-medium font-h6 group-hover:translate-x-1 transition duration-400">
				{translations[status]}
			</Heading6>
		</div>
	)
}
